import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import { HowToRegOutlined, WarningAmber } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import ConfirmChangesDialog from "components/common/ConfirmChangesDialog";
import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import StepHeading from "components/userManagement/form/StepHeading";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureAccessPreview,
  type SecureProject,
  useGetSecureProjectCandidatesQuery,
  useGetSecureProjectMembersQuery,
  useLazyGetSecureProjectAccessPreviewQuery,
  useSetSecureProjectMembersMutation,
} from "features/secureUploads/secureUploads";
import { useViewAs } from "hooks/useViewAs";
import { formatApiError } from "utils/formatApiError";

import { previewSections } from "./accessChanges";
import ProjectAccessEditor from "./ProjectAccessEditor";
import { sameMembers } from "./projectModel";

interface ManageProjectAccessDialogProps {
  open: boolean;
  project: SecureProject | null;
  onClose: () => void;
}

/** Who is on a project, saved through a last look at what each person gains or
 * loses. A closed project keeps this: closing changes the work, not the access. */
export default function ManageProjectAccessDialog({
  open,
  project,
  onClose,
}: ManageProjectAccessDialogProps) {
  const dispatch = useDispatch();
  const { readOnly } = useViewAs();
  const skip = !open || !project;
  const { data: members, isFetching: loadingMembers } =
    useGetSecureProjectMembersQuery(project?.id ?? 0, { skip });
  const { data: candidates = [], isFetching: loadingCandidates } =
    useGetSecureProjectCandidatesQuery(project?.client_id ?? 0, { skip });
  const [loadPreview, { isFetching: previewing }] =
    useLazyGetSecureProjectAccessPreviewQuery();
  const [saveMembers, { isLoading: saving }] =
    useSetSecureProjectMembersMutation();

  const initial = (members ?? []).map((person) => person.id);
  const [selected, setSelected] = useState<number[]>([]);
  const [preview, setPreview] = useState<SecureAccessPreview | null>(null);

  useEffect(() => {
    if (open) setSelected((members ?? []).map((person) => person.id));
    if (!open) setPreview(null);
  }, [open, members]);

  if (!project) return null;
  const unsaved = !sameMembers(initial, selected);
  const closed = project.status === "closed";
  const busy = saving || previewing || loadingMembers;

  const toast = (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));

  const review = async () => {
    try {
      setPreview(
        await loadPreview({ id: project.id, member_ids: selected }).unwrap(),
      );
    } catch (error) {
      toast(
        formatApiError(
          (error as { data?: unknown })?.data,
          "Could not check the changes.",
        ),
        "error",
      );
    }
  };

  const confirm = async () => {
    try {
      await saveMembers({ id: project.id, member_ids: selected }).unwrap();
      toast("Project access saved.", "success");
      setPreview(null);
      onClose();
    } catch (error) {
      setPreview(null);
      toast(
        formatApiError(
          (error as { data?: unknown })?.data,
          "Could not save the access.",
        ),
        "error",
      );
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <FormHeader
            avatar={<HowToRegOutlined fontSize="small" />}
            title="Manage Project Access"
            onClose={onClose}
            closeDisabled={busy}
          />
          <Box sx={{ p: 3, overflowY: "auto" }}>
            {closed && (
              <Box
                sx={{
                  mb: 2,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "#FDECEA",
                  borderRadius: 1,
                }}
              >
                <WarningAmber sx={{ fontSize: 18, color: "error.main" }} />
                <Typography variant="caption" sx={{ color: "error.dark" }}>
                  This project is currently closed. Users <b>will not</b> be
                  able to upload new files.
                </Typography>
              </Box>
            )}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              {project.name}
            </Typography>
            <StepHeading
              title="Project Access"
              subtitle="Edit which client users can view and collaborate on this project."
            />
            <Box sx={{ mt: 2 }}>
              <ProjectAccessEditor
                candidates={candidates}
                selected={selected}
                onChange={setSelected}
                showConfidential
                loading={loadingCandidates}
                disabled={busy || readOnly}
              />
            </Box>
          </Box>
        </DialogContent>
        <FormFooter
          mode="edit"
          isFirstStep
          unsaved={unsaved}
          onBack={() => undefined}
          onCancel={onClose}
        >
          <ReadOnlyTooltip active={readOnly}>
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={review}
                disabled={!unsaved || busy || readOnly}
                sx={FOOTER_BUTTON_SX}
              >
                Save Changes
              </Button>
            </span>
          </ReadOnlyTooltip>
        </FormFooter>
      </Dialog>
      <ConfirmChangesDialog
        open={preview !== null}
        intro={
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {project.name}
            </Typography>
            <Typography variant="body2">
              Nothing has been saved yet. Review the changes below before
              confirming.
            </Typography>
          </Box>
        }
        sections={preview ? previewSections(preview) : []}
        confirmDisabled={saving}
        onCancel={() => setPreview(null)}
        onConfirm={confirm}
      />
    </>
  );
}
